import {Injectable} from '@angular/core';
import {ModalController, Platform} from '@ionic/angular';
import {Observable} from 'rxjs';
import {environment} from '../../environments/environment';
import {HttpClient} from '@angular/common/http';
import {PermissionPage} from '../pages/permission/permission.page';
import moment from 'moment';
import {TrackingService} from './tracking.service';
import {ETA_STATUS, IReservation} from '../models/IReservation';
import {RtcService} from './rtc.service';
import {ReservationsService} from './reservations.service';
import {PermissionsService} from './permissions.service';
import {CapacitorGeolocationService} from './capacitor-geolocation.service';

declare const H: any;

export interface IEtaPosition {
  reservationId: string;
  coordinates: {
    locationX: number;
    locationY: number;
    positionDateTime: string;
    speed: number;
    heading: number;
    accuracy?: number;
  }
}

@Injectable({
  providedIn: 'root'
})
export class EtaService {
  private geoposition: GeolocationPosition;
  private recentlySent: IEtaPosition;
  private reservationId: string;
  private etaEnabled: boolean;
  private etaInterval: any;
  private reservation: IReservation;

  constructor(
    private rtcService: RtcService,
    private geolocation: CapacitorGeolocationService,
    private trackingService: TrackingService,
    private permissionsService: PermissionsService,
    private modalController: ModalController,
    private reservationsService: ReservationsService,
    private http: HttpClient,
    public platform: Platform
  ) {
    this.etaRestoreState();
    this.init();
  }

  etaRestoreState() {
    this.etaEnabled = this.trackingService.getEtaEnabled();
    this.reservationId = this.trackingService.getReservationId();
    this.fetchReservation();
    this.rtcService.reservations$.subscribe((reservation: IReservation) => {
      if (this.reservationId === reservation.id) {
        console.log('rtcService', reservation);
        this.reservation = reservation;
        this.checkEtaStatus();
      }
    });
  }

  async fetchReservation() {
    if (this.reservationId) {
      this.reservation = await this.reservationsService.getReservationById(this.reservationId).toPromise();
      this.checkEtaStatus();
    }
  }

  checkEtaStatus() {
    if (this.reservation.etaStatus === ETA_STATUS.cancelled && this.etaEnabled) {
      console.log('checkEtaStatus etaCancel', this.reservationId);
      this.etaCancel(this.reservationId);
    } else if (this.reservation.etaStatus === ETA_STATUS.paused && this.etaEnabled) {
      console.log('checkEtaStatus etaPause', this.reservationId);
      this.etaPause(this.reservationId);
    }
  }

  async init() {
    if (this.platform.is('hybrid')) {
      await this.initGeolocationAfterPermissionCheck();
    } else {
      this.initGeolocation();
    }
    this.setBrowserEtaInterval();

    this.rtcService.reservations$.pipe().subscribe((reservation: IReservation) => {
      console.log('eta reservations$', reservation);
      if (this.reservationId === reservation.id && reservation.eta) {
        this.setEtaIntervalAccordingEta(reservation);
      }
    });
  }

  public etaReservation() {
    return this.reservationId;
  }

  public async etaStart(reservationId: string, accessToken: string): Promise<any> {
    console.log('etaStart', reservationId);
    this.reservationId = reservationId;
    this.trackingService.setReservationId(reservationId);
    this.etaEnableState(reservationId);
    this.trackingService.updateConfig({
      url: this.getEtaPositionUrl(reservationId),
      locationTemplate: this.trackingService.getEtaRequest(reservationId),
    });
    this.trackingService.startTracking();

    if (this.platform.is('hybrid')) {
      await this.checkForPermissionAndStart();
    }

    const result = await this.http.post<any>(`${environment.apiUrl}/reservations/${reservationId}/eta/start`, {
      reservationId,
      silent: true
    }).toPromise();
    await this.sendCurrentPosition();

    return result;
  }

  public etaPause(reservationId: string): Promise<any> {
    this.reservationId = reservationId;
    this.etaDisableState();
    const url = `${environment.apiUrl}/reservations/${reservationId}/eta/pause`;

    return this.http.post<any>(url, {
      reservationId,
      silent: true
    }).toPromise();
  }

  public isTracking(): boolean {
    return this.trackingService.isTrackingStarted();
  }

  public etaCancel(reservationId: string): Observable<any> {
    this.etaDisableState();

    return this.http.post<any>(`${environment.apiUrl}/reservations/${reservationId}/eta/cancel`, {
      reservationId,
      silent: true
    });
  }

  etaDisableState() {
    this.etaEnabled = false;
    this.reservationId = '';
    this.trackingService.setEtaEnabled(false);
    this.trackingService.setReservationId('');
    this.trackingService.setTrackingStatus(false);
    this.trackingService.stopTracking();
  }

  etaEnableState(reservationId: string) {
    this.etaEnabled = true;
    this.reservationId = reservationId;
    this.trackingService.setEtaEnabled(true);
    this.trackingService.setReservationId(reservationId);
    this.trackingService.setTrackingStatus(true);
  }

  arrived(reservationId: string) {
    this.etaDisableState();
    return this.http.post<any>(`${environment.apiUrl}/reservations/${reservationId}/eta/arrived`, this.recentlySent);
  }

  public getEtaPositionUrl(reservationId: string) {
    // used for debugging purpose
    // return 'http://172.104.242.116:9000/eta/position';
    return `${environment.apiUrl}/reservations/${reservationId}/eta/position`;
  }

  public etaPosition(position: IEtaPosition): Observable<any> {
    console.log('etaPosition', position);
    return this.http.post<any>(this.getEtaPositionUrl(position.reservationId), {
      ...position,
      silent: true
    });
  }

  async initGeolocationAfterPermissionCheck() {
    const hasPermission = await this.permissionsService.hasLocationPermission();
    if (!hasPermission) {
      const modal = await this.modalController.create({
        component: PermissionPage
      });
      await modal.present();
      const modalResult = await modal.onWillDismiss();
      if (modalResult?.data?.permissionGranted) {
        this.initGeolocation();
      }
    } else {
      this.initGeolocation();
    }
  }

  public getGeoposition() {
    return this.geoposition;
  }

  public getRecentlySent() {
    return this.recentlySent;
  }

  // meters
  public distanceToLocation(reservation: IReservation) {
    if (!reservation?.location?.coordinates?.latitude || !this.geoposition?.coords?.latitude) {
      return null;
    }

    const p1 = new H.geo.Point(
      this.geoposition?.coords.latitude,
      this.geoposition?.coords.longitude
    );

    const p2 = new H.geo.Point(
      reservation.location.coordinates.latitude,
      reservation.location.coordinates.longitude
    );

    return Math.floor(p1.distance(p2));
  }

  public isDistanceTooFar(reservation: IReservation) {
    const distance = reservation.location.settings.etaArrivingGeofence || 2000;
    return this.distanceToLocation(reservation) > distance;
  }

  private setEtaIntervalAccordingEta(reservation) {
    console.log('setEtaIntervalAccordingEta', reservation);
    const minutes = moment(reservation.eta).diff(moment(), 'minutes');
    let interval = 120;
    // eta time in the next 30 min or below -120 seconds
    // eta time in the next 30-120 min - 300 seconds
    // eta time more than 120 min upfront - 600 seconds
    if (minutes < 30) {
      interval = 120;
    } else if (minutes <= 120) {
      interval = 300;
    } else if (minutes > 120) {
      interval = 600;
    }
    this.setBrowserEtaInterval();
    this.trackingService.setInterval(interval);
  }

  private setBrowserEtaInterval() {
    clearInterval(this.etaInterval);
    this.etaInterval = setInterval(() => this.etaIntervalHandler(), 600 * 1000);
    this.etaIntervalHandler();
  }

  private async etaIntervalHandler() {
    if (this.etaEnabled && this.reservationId && this.geoposition) {
      const req: IEtaPosition = {
        reservationId: this.reservationId,
        coordinates: {
          locationX: this.geoposition?.coords.longitude,
          locationY: this.geoposition?.coords.latitude,
          positionDateTime: moment().utc().locale('en').format('YYYY-MM-DDTHH:mm:ss'),
          speed: Math.max(0, this.geoposition?.coords.speed),
          heading: Math.max(0, this.geoposition?.coords.heading),
          accuracy: Math.max(0, this.geoposition?.coords.accuracy)
        }
      };
      await this.etaPosition(req).toPromise();
      this.recentlySent = req;
    }
  }

  private async sendCurrentPosition() {
    try {
      this.geoposition = await this.geolocation.getCurrentPosition({
        maximumAge: 60000,
        enableHighAccuracy: true,
        timeout: 5000
      });
    } catch (e) {
      console.warn(e);
    }
    await this.etaIntervalHandler();
  }

  private initGeolocation() {
    this.geolocation.watchPosition({enableHighAccuracy: true, timeout: 10000, maximumAge: 60000})
      .subscribe((geoposition: GeolocationPosition) => {
        if (geoposition?.coords) {
          this.geoposition = geoposition;
        }
      }, (e) => console.warn(e));

    setTimeout(() => {
      this.geolocation.getCurrentPosition({
        maximumAge: 60000,
        enableHighAccuracy: true,
        timeout: 5000
      }).then((val) => {
        this.geoposition = val;
        this.etaIntervalHandler();
      }).catch((e) => console.warn(e));
    }, 100);
  }

  private async checkForPermissionAndStart() {
    const hasPermission = await this.permissionsService.hasBackgroundLocationPermission();
    if (!hasPermission) {
      const modal = await this.modalController.create({
        component: PermissionPage,
        componentProps: {
          'allowBackgroundLocation': true
        }
      });
      await modal.present();
      const modalResult = await modal.onWillDismiss();
      if (modalResult && modalResult.data && modalResult.data.permissionGranted) {
        this.trackingService.configureBackgroundGeolocation(this.trackingService.getConfig());
      }
    } else {
      this.trackingService.configureBackgroundGeolocation(this.trackingService.getConfig());
    }
  }
}
